/*
 * ct-mobile-fixes.js
 * ------------------------------------------------------------------
 * Behaviour for the mobile sticky add-to-cart bar and the size-help
 * dialog. Deliberately dependency-free and defensive: if any element
 * is missing the script does nothing rather than throwing.
 *
 * The sticky bar never re-implements add-to-cart. It forwards the tap
 * to the real submit button so all of Dawn's existing validation,
 * cart-drawer opening and error handling still applies.
 * ------------------------------------------------------------------
 */
(function () {
  'use strict';

  var MOBILE = '(max-width: 749px)';

  /* ---------------------------------------------------------------
   * Sticky add to cart
   * ------------------------------------------------------------- */
  function initStickyAtc(bar) {
    var sectionId = bar.getAttribute('data-section-id');
    if (!sectionId) return;

    var realButton = document.getElementById('ProductSubmitButton-' + sectionId);
    var priceHost = document.getElementById('price-' + sectionId);
    var stickyButton = bar.querySelector('[data-ct-atc-proxy]');
    var stickyPrice = bar.querySelector('[data-ct-atc-price]');
    var stickyLabel = stickyButton ? stickyButton.querySelector('span') : null;

    if (!realButton || !stickyButton) {
      bar.remove();
      return;
    }

    /* --- forward the tap ---------------------------------------- */
    stickyButton.addEventListener('click', function (event) {
      event.preventDefault();
      if (realButton.hasAttribute('disabled')) return;
      realButton.click();
    });

    /* --- mirror the real button's label and disabled state ------- */
    function syncButton() {
      var disabled = realButton.hasAttribute('disabled');
      if (disabled) {
        stickyButton.setAttribute('disabled', 'disabled');
      } else {
        stickyButton.removeAttribute('disabled');
      }
      var realLabel = realButton.querySelector('span');
      if (stickyLabel && realLabel) {
        var text = realLabel.textContent.trim();
        if (text) stickyLabel.textContent = text;
      }
    }

    /* --- mirror the price --------------------------------------- */
    function syncPrice() {
      if (!stickyPrice || !priceHost) return;
      var regular = priceHost.querySelector('.price-item--regular');
      var sale = priceHost.querySelector('.price__sale .price-item--sale');
      var compare = priceHost.querySelector('.price__sale .price-item--regular');
      var onSale = priceHost.classList.contains('price--on-sale');

      var main = onSale && sale ? sale : regular;
      if (!main) return;

      var html = '<span>' + main.textContent.trim() + '</span>';
      if (onSale && compare && compare.textContent.trim()) {
        html += '<s class="ct-compare">' + compare.textContent.trim() + '</s>';
      }
      stickyPrice.innerHTML = html;
    }

    syncButton();
    syncPrice();

    /* Dawn re-renders the buy box on variant change, so watch for it
       rather than hooking a private pub/sub channel. */
    var observed = realButton.closest('.product-form__buttons') || realButton;
    var buttonWatcher = new MutationObserver(syncButton);
    buttonWatcher.observe(observed, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['disabled', 'class'],
    });

    if (priceHost) {
      var priceWatcher = new MutationObserver(syncPrice);
      priceWatcher.observe(priceHost, { subtree: true, childList: true, characterData: true });
    }

    /* --- show the bar only once the real button is off screen ---- */
    var anchor = realButton.closest('.product-form__buttons') || realButton;
    var shouldShow = false;

    function render() {
      var mobile = window.matchMedia(MOBILE).matches;
      var drawerOpen =
        document.body.classList.contains('overflow-hidden-mobile') ||
        document.body.classList.contains('overflow-hidden-tablet') ||
        !!document.querySelector('cart-drawer.active, .menu-drawer-container[open], dialog[open]');
      var visible = mobile && shouldShow && !drawerOpen;
      bar.setAttribute('data-visible', visible ? 'true' : 'false');
      bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            shouldShow = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          });
          render();
        },
        { rootMargin: '0px 0px -40% 0px', threshold: 0 }
      ).observe(anchor);
    }

    window.addEventListener('resize', render, { passive: true });
    document.addEventListener('click', function () {
      window.setTimeout(render, 60);
    });

    document.body.classList.add('ct-has-sticky-atc');

    /* Publish the real height so the chat launcher and footer can
       clear it, rather than everyone hard-coding 68px. */
    function publishHeight() {
      var h = Math.round(bar.getBoundingClientRect().height);
      if (h > 0) document.documentElement.style.setProperty('--ct-sticky-atc-height', h + 'px');
    }
    publishHeight();
    window.addEventListener('resize', publishHeight, { passive: true });

    render();
  }

  /* ---------------------------------------------------------------
   * Size help dialog
   * ------------------------------------------------------------- */
  function initSizeHelp() {
    var dialog = document.querySelector('[data-ct-size-modal]');
    if (!dialog) return;

    var openers = document.querySelectorAll('[data-ct-size-open]');
    if (!openers.length) return;

    var supportsDialog = typeof dialog.showModal === 'function';

    openers.forEach(function (opener) {
      opener.addEventListener('click', function () {
        if (supportsDialog) {
          dialog.showModal();
        } else {
          dialog.setAttribute('open', 'open');
        }
      });
    });

    dialog.querySelectorAll('[data-ct-size-close]').forEach(function (closer) {
      closer.addEventListener('click', function () {
        if (supportsDialog) {
          dialog.close();
        } else {
          dialog.removeAttribute('open');
        }
      });
    });

    /* Click on the backdrop closes it. */
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) {
        if (supportsDialog) dialog.close();
        else dialog.removeAttribute('open');
      }
    });
  }

  /* ---------------------------------------------------------------
   * Collapsible product description
   *
   * Only marks a description as collapsible once it is measured as
   * genuinely overflowing, so short descriptions never get a pointless
   * "Read more". If this never runs, the CSS leaves the text fully
   * expanded rather than permanently truncated.
   * ------------------------------------------------------------- */
  function initCollapsibleDescription(host) {
    var inner = host.querySelector('.ct-desc__inner');
    var toggle = host.querySelector('[data-ct-desc-toggle]');
    var label = host.querySelector('[data-ct-desc-label]');
    if (!inner || !toggle) return;

    var COLLAPSED_PX = 330; // must stay in step with max-height in the CSS
    var LABEL_MORE = 'Read full description';
    var LABEL_LESS = 'Show less';

    function measure() {
      if (!window.matchMedia(MOBILE).matches) {
        host.removeAttribute('data-ct-overflowing');
        return;
      }
      // Measure against the uncollapsed height.
      var wasCollapsed = host.getAttribute('data-ct-overflowing') === 'true';
      if (wasCollapsed) host.removeAttribute('data-ct-overflowing');
      var full = inner.scrollHeight;
      if (full > COLLAPSED_PX + 80) {
        host.setAttribute('data-ct-overflowing', 'true');
      } else {
        host.removeAttribute('data-ct-overflowing');
        host.removeAttribute('data-ct-expanded');
      }
    }

    toggle.addEventListener('click', function () {
      var expanded = host.getAttribute('data-ct-expanded') === 'true';
      if (expanded) {
        host.removeAttribute('data-ct-expanded');
        toggle.setAttribute('aria-expanded', 'false');
        if (label) label.textContent = LABEL_MORE;
        // Keep the toggle in view when collapsing a long block.
        var top = host.getBoundingClientRect().top + window.scrollY - 90;
        window.scrollTo({ top: top, behavior: 'smooth' });
      } else {
        host.setAttribute('data-ct-expanded', 'true');
        toggle.setAttribute('aria-expanded', 'true');
        if (label) label.textContent = LABEL_LESS;
      }
    });

    measure();
    window.addEventListener('resize', measure, { passive: true });

    // Images loading late change the height.
    inner.querySelectorAll('img').forEach(function (img) {
      if (!img.complete) img.addEventListener('load', measure, { once: true });
    });
  }

  /* ---------------------------------------------------------------
   * Boot
   * ------------------------------------------------------------- */
  function boot() {
    document.querySelectorAll('[data-ct-sticky-atc]').forEach(initStickyAtc);
    document.querySelectorAll('[data-ct-collapsible]').forEach(initCollapsibleDescription);
    initSizeHelp();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
